import { escapeMarkdownLinkText, normalizeInternalLinkValue, parseInternalHref, } from "./internal-link-token.js";
export const DOCUMENT_EVIDENCE_PATH = "/__df_evidence__";
/**
 * What a citation is called when its node carries no label of its own. The editor's chip,
 * the markdown serializers here and the export adapter all fall back to this one string.
 */
export const DEFAULT_DOCUMENT_EVIDENCE_LABEL = "Document evidence";
/** Readable stand-in for a reserved evidence path that appears as prose rather than a link. */
export const DOCUMENT_EVIDENCE_REDACTION_TEXT = "document evidence";
/**
 * Any bare occurrence of the reserved path, including near misses such as
 * `/__df_evidence__/?…` and `/__df_evidence__extra?…`. Nothing legitimate lives under the
 * `__df_` namespace, so the whole run is consumed rather than left as a pseudo-URL.
 */
const BARE_DOCUMENT_EVIDENCE_PATH_PATTERN = /\/__df_evidence__[^\s)\]},.!?;:`'"<>]*(?:\?[^\s)\]},.!?;:`'"<>]*)?/g;
function redactBarePaths(value, replacement) {
    return value.replaceAll(BARE_DOCUMENT_EVIDENCE_PATH_PATTERN, replacement);
}
/** End index (exclusive) of the inline code span opening at `start`, or null. */
function readCodeSpanEnd(value, start) {
    let fence = 0;
    while (value[start + fence] === "`")
        fence += 1;
    const delimiter = "`".repeat(fence);
    let cursor = start + fence;
    while (cursor < value.length) {
        const next = value.indexOf(delimiter, cursor);
        if (next < 0)
            return null;
        if (value[next + fence] === "`") {
            cursor = next + fence;
            while (value[cursor] === "`")
                cursor += 1;
            continue;
        }
        return next + fence;
    }
    return null;
}
/** Scans a bracketed run from `start`, honouring backslash escapes and nesting. */
function readBalanced(value, start, open, close) {
    if (value[start] !== open)
        return null;
    let depth = 0;
    for (let cursor = start; cursor < value.length; cursor += 1) {
        const char = value[cursor];
        if (char === "\\") {
            cursor += 1;
            continue;
        }
        if (char === open)
            depth += 1;
        else if (char === close) {
            depth -= 1;
            if (depth === 0)
                return cursor + 1;
        }
    }
    return null;
}
/**
 * Replaces reserved evidence paths wherever they would be *visible* — in prose, inside
 * inline code, and in Markdown link labels — while preserving the destination of a
 * reserved evidence link so the renderer can still classify it.
 *
 * Model-authored text is untrusted and locator identifiers are internal transport, so
 * every surface that renders it passes through here. Two deliberate choices:
 *
 * - It works on the source string, not a parsed AST. Markdown consumes the `__` in
 *   `/__df_evidence__` as emphasis, so by the time an AST exists the path is already
 *   split across nodes and can no longer be matched.
 * - It scans rather than pattern-matches links. A regex cannot honour the `\]` escapes
 *   that `serializeDocumentEvidenceToken` emits for a document name containing brackets,
 *   and mis-parsing such a token destroyed its destination and with it the chip.
 */
export function redactVisibleDocumentEvidencePaths(value, replacement = DOCUMENT_EVIDENCE_REDACTION_TEXT) {
    if (!value.includes(DOCUMENT_EVIDENCE_PATH))
        return value;
    let result = "";
    let plain = "";
    let cursor = 0;
    const flushPlain = () => {
        result += redactBarePaths(plain, replacement);
        plain = "";
    };
    while (cursor < value.length) {
        const char = value[cursor];
        // An escaped character can never open a construct, so carry it through untouched.
        if (char === "\\" && cursor + 1 < value.length) {
            plain += value.slice(cursor, cursor + 2);
            cursor += 2;
            continue;
        }
        if (char === "`") {
            const end = readCodeSpanEnd(value, cursor);
            if (end !== null) {
                flushPlain();
                // A locator inside code can never become a chip, so it is redacted like prose.
                result += redactBarePaths(value.slice(cursor, end), replacement);
                cursor = end;
                continue;
            }
        }
        if (char === "[") {
            const labelEnd = readBalanced(value, cursor, "[", "]");
            const destinationEnd = labelEnd !== null && value[labelEnd] === "("
                ? readBalanced(value, labelEnd, "(", ")")
                : null;
            if (labelEnd !== null && destinationEnd !== null) {
                flushPlain();
                const label = value.slice(cursor + 1, labelEnd - 1);
                const destination = value.slice(labelEnd + 1, destinationEnd - 1);
                // The label is visible text; only a reserved destination is worth protecting,
                // and the renderers turn those into a chip or inert text but never an anchor.
                const safeDestination = classifyDocumentEvidenceHref(destination).kind === "unrelated"
                    ? redactBarePaths(destination, replacement)
                    : destination;
                result += `[${redactVisibleDocumentEvidencePaths(label, replacement)}](${safeDestination})`;
                cursor = destinationEnd;
                continue;
            }
        }
        plain += char;
        cursor += 1;
    }
    flushPlain();
    return result;
}
const SPREADSHEET_EXTENSIONS = new Set(["xlsx", "xlsm", "xlsb", "xls", "csv", "tsv", "ods"]);
const CODE_EXTENSIONS = new Set([
    "md",
    "markdown",
    "json",
    "jsonl",
    "yaml",
    "yml",
    "toml",
    "xml",
    "sql",
    "py",
    "ipynb",
    "js",
    "jsx",
    "ts",
    "tsx",
    "sh",
    "bash",
    "ps1",
    "r",
    "scala",
    "java",
    "cs",
    "go",
    "rb",
    "php",
    "html",
    "css",
]);
/**
 * The glyph for a cited document, from what the upload actually was. The mime type is
 * consulted first because it is what the uploader declared; the file name's extension is
 * the fallback, since a display name may be a document title with no extension at all.
 * Anything unrecognized reads as prose, which is what most cited documents are.
 */
export function resolveEvidenceFileKind(source) {
    const mimeType = source.mimeType?.trim().toLowerCase() ?? "";
    if (mimeType) {
        if (mimeType.includes("spreadsheet") ||
            mimeType.includes("excel") ||
            mimeType === "text/csv" ||
            mimeType === "text/tab-separated-values") {
            return "spreadsheet";
        }
        if (mimeType === "text/markdown" ||
            mimeType === "application/json" ||
            mimeType === "application/xml" ||
            mimeType === "text/xml" ||
            mimeType.startsWith("application/x-")) {
            return "code";
        }
    }
    const extension = (source.fileName ?? "").trim().toLowerCase().split(".").pop() ?? "";
    if (SPREADSHEET_EXTENSIONS.has(extension))
        return "spreadsheet";
    if (CODE_EXTENSIONS.has(extension))
        return "code";
    return "text";
}
/**
 * Batching bound for `documentEvidence.present`. The backend rejects a longer call and the
 * frontend chunks its registered locators to match, so the two must agree.
 */
export const DOCUMENT_EVIDENCE_PRESENTATION_LIMIT = 50;
/**
 * Batching bound for `documentEvidence.resolveSourceUrls`. Far tighter than the
 * presentation limit: source URLs are minted when a reader opens one citation group, not
 * for every citation on screen.
 */
export const DOCUMENT_EVIDENCE_SOURCE_URL_LIMIT = 20;
/**
 * Batching bound for `documentEvidence.resolveFigureImageUrls`. Same reasoning as the
 * source-URL limit: image URLs are minted when a reader opens one citation group, not for
 * every citation on screen.
 */
export const DOCUMENT_EVIDENCE_FIGURE_IMAGE_LIMIT = 20;
function normalizeSpan(span) {
    if (!span ||
        !Number.isInteger(span.startChar) ||
        !Number.isInteger(span.endChar) ||
        span.startChar < 0 ||
        span.startChar >= span.endChar) {
        return undefined;
    }
    return { startChar: span.startChar, endChar: span.endChar };
}
function hasOnlyKnownParams(params) {
    const known = new Set(["artifactId", "processingRevisionId", "blockId", "startChar", "endChar"]);
    for (const key of params.keys()) {
        if (!known.has(key))
            return false;
    }
    return true;
}
/**
 * Returns `null` when the parameter is absent and `NaN` when it is present but not a
 * canonical non-negative decimal integer. `Number()` alone would accept `0x10`, `1e3`,
 * and `" 5 "`, letting a tampered token silently change evidence granularity.
 */
function parseOptionalInteger(value) {
    if (value === null)
        return null;
    if (!/^\d+$/.test(value))
        return Number.NaN;
    const parsed = Number(value);
    return Number.isSafeInteger(parsed) ? parsed : Number.NaN;
}
export function buildDocumentEvidenceHref(reference) {
    const artifactId = normalizeInternalLinkValue(reference.artifactId);
    const processingRevisionId = normalizeInternalLinkValue(reference.processingRevisionId);
    const blockId = normalizeInternalLinkValue(reference.blockId);
    if (reference.kind !== "document_block" || !artifactId || !processingRevisionId || !blockId) {
        return null;
    }
    const span = reference.span ? normalizeSpan(reference.span) : undefined;
    if (reference.span && !span)
        return null;
    const params = new URLSearchParams({
        artifactId,
        processingRevisionId,
        blockId,
    });
    if (span) {
        params.set("startChar", String(span.startChar));
        params.set("endChar", String(span.endChar));
    }
    return `${DOCUMENT_EVIDENCE_PATH}?${params.toString()}`;
}
export function serializeDocumentEvidenceToken(token) {
    const label = normalizeInternalLinkValue(token.label) || DEFAULT_DOCUMENT_EVIDENCE_LABEL;
    const href = buildDocumentEvidenceHref(token);
    // Escape on both branches: an unescapable label such as `Report](x` would otherwise
    // emit stray link syntax when the token degrades to plain text.
    return href ? `[${escapeMarkdownLinkText(label)}](${href})` : escapeMarkdownLinkText(label);
}
/**
 * The node-attrs counterpart of {@link serializeDocumentEvidenceToken} and
 * {@link buildDocumentEvidenceNodeAttrs}: it takes what a `documentEvidence` node stores and
 * emits the token that parses back into the same node.
 *
 * Every serializer that meets stored content — the read path's `serializeNodesToMarkdown` and
 * the write path's draft serializer — goes through here, because a citation is only durable if
 * the surface the model reads emits exactly what the compiler accepts back (LAT-989).
 *
 * A node missing any locator field cannot address a block, so it degrades to its label. The
 * label is escaped on that branch for the same reason the token's own label-only branch escapes
 * it: `Report](x` would otherwise emit stray link syntax.
 */
export function serializeDocumentEvidenceNode(attrs) {
    const label = attrs?.label;
    const artifactId = attrs?.artifactId;
    const processingRevisionId = attrs?.processingRevisionId;
    const blockId = attrs?.blockId;
    if (typeof label !== "string" ||
        typeof artifactId !== "string" ||
        typeof processingRevisionId !== "string" ||
        typeof blockId !== "string") {
        return escapeMarkdownLinkText(normalizeInternalLinkValue(label) || DEFAULT_DOCUMENT_EVIDENCE_LABEL);
    }
    const startChar = attrs?.startChar;
    const endChar = attrs?.endChar;
    return serializeDocumentEvidenceToken({
        kind: "document_block",
        label,
        artifactId,
        processingRevisionId,
        blockId,
        ...(typeof startChar === "number" && typeof endChar === "number"
            ? { span: { startChar, endChar } }
            : {}),
    });
}
export function classifyDocumentEvidenceHref(href) {
    if (!href)
        return { kind: "unrelated" };
    // The prefix is reserved: once an href claims it, it can only be valid evidence or
    // reserved-but-invalid. Falling back to `unrelated` here would let near-miss paths
    // such as `/__df_evidence__/?...` or `/__df_evidence__extra?...` render as ordinary
    // in-app anchors exposing raw locator IDs.
    if (!href.startsWith(DOCUMENT_EVIDENCE_PATH))
        return { kind: "unrelated" };
    const parsed = parseInternalHref(href);
    if (!parsed || parsed.pathname !== DOCUMENT_EVIDENCE_PATH) {
        return { kind: "reserved_invalid" };
    }
    if (parsed.hash ||
        !hasOnlyKnownParams(parsed.searchParams) ||
        [...parsed.searchParams.keys()].some((key, index, keys) => keys.indexOf(key) !== index)) {
        return { kind: "reserved_invalid" };
    }
    const artifactId = normalizeInternalLinkValue(parsed.searchParams.get("artifactId"));
    const processingRevisionId = normalizeInternalLinkValue(parsed.searchParams.get("processingRevisionId"));
    const blockId = normalizeInternalLinkValue(parsed.searchParams.get("blockId"));
    const startChar = parseOptionalInteger(parsed.searchParams.get("startChar"));
    const endChar = parseOptionalInteger(parsed.searchParams.get("endChar"));
    const hasStart = startChar !== null;
    const hasEnd = endChar !== null;
    if (!artifactId ||
        !processingRevisionId ||
        !blockId ||
        hasStart !== hasEnd ||
        Number.isNaN(startChar) ||
        Number.isNaN(endChar)) {
        return { kind: "reserved_invalid" };
    }
    const span = hasStart && hasEnd
        ? normalizeSpan({ startChar: startChar, endChar: endChar })
        : undefined;
    if (hasStart && !span) {
        return { kind: "reserved_invalid" };
    }
    return {
        kind: "valid",
        reference: {
            kind: "document_block",
            artifactId,
            processingRevisionId,
            blockId,
            ...(span ? { span } : {}),
        },
    };
}
export function parseDocumentEvidenceHref(href) {
    const classified = classifyDocumentEvidenceHref(href);
    return classified.kind === "valid" ? classified.reference : null;
}
export function buildDocumentEvidenceNodeAttrs(token) {
    return {
        label: normalizeInternalLinkValue(token.label) || DEFAULT_DOCUMENT_EVIDENCE_LABEL,
        artifactId: normalizeInternalLinkValue(token.artifactId),
        processingRevisionId: normalizeInternalLinkValue(token.processingRevisionId),
        blockId: normalizeInternalLinkValue(token.blockId),
        ...(token.span
            ? {
                startChar: token.span.startChar,
                endChar: token.span.endChar,
            }
            : {}),
    };
}
